import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldCheck, Truck, Sparkles } from "lucide-react";
import { HAT_GALLERY } from "../lib/assets";
import { BuyButton } from "../components/BuyButton";
import { MaskLines, Reveal } from "../components/Motion";
import { Footer } from "../components/Footer";
import { useProduct } from "../hooks/useProduct";
import { money, shipText } from "../lib/api";

const Gallery = () => {
  const [active, setActive] = useState(0);
  return (
    <div>
      <div className="relative bg-white border-2 border-[#0B0B0B] overflow-hidden aspect-[4/5] shadow-[10px_10px_0_#E10600]" data-testid="product-main-image">
        {HAT_GALLERY.map((g, i) => (
          <motion.img
            key={g.src}
            src={g.src}
            alt={`Project X hat — ${g.label}`}
            className="absolute inset-0 w-full h-full object-cover"
            initial={false}
            animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.06 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}
        <span className="absolute top-4 left-4 bg-[#E10600] text-white font-mono-x text-xs uppercase tracking-[0.2em] px-3 py-1.5">Latest update</span>
      </div>
      <div className="grid grid-cols-4 gap-3 mt-6">
        {HAT_GALLERY.map((g, i) => (
          <button
            key={g.src}
            onClick={() => setActive(i)}
            data-testid={`product-thumb-${i}`}
            className={`relative aspect-square overflow-hidden border-2 bg-white transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${active === i ? "border-[#E10600]" : "border-zinc-300"}`}
          >
            <img src={g.src} alt={g.label} className="w-full h-full object-cover" />
            <span className="absolute bottom-0 inset-x-0 bg-white/90 font-mono-x text-[11px] uppercase tracking-widest py-1">{g.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const Qty = ({ value, onChange, max }) => (
  <div className="inline-flex items-center border-2 border-[#0B0B0B]" data-testid="quantity-selector">
    <button data-testid="qty-decrease" onClick={() => onChange(Math.max(1, value - 1))} className="p-4 transition-colors duration-200 hover:bg-[#0B0B0B] hover:text-white" aria-label="Decrease"><Minus className="w-4 h-4" /></button>
    <span data-testid="qty-value" className="w-14 text-center font-display text-2xl">{value}</span>
    <button data-testid="qty-increase" onClick={() => onChange(Math.min(max, value + 1))} className="p-4 transition-colors duration-200 hover:bg-[#0B0B0B] hover:text-white" aria-label="Increase"><Plus className="w-4 h-4" /></button>
  </div>
);

export default function Shop() {
  const [product] = useProduct();
  const [qty, setQty] = useState(1);
  const max = Math.max(1, Math.min(10, product?.stock ?? 10));
  const subtotal = (product?.price ?? 0) * qty;

  return (
    <main data-testid="shop-page" className="bg-[#F6F3EF]">
      <section className="px-5 sm:px-10 lg:px-16 pt-32 pb-24 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6"><Reveal><Gallery /></Reveal></div>
        <div className="lg:col-span-6 lg:pl-6">
          <p className="font-mono-x uppercase text-xs tracking-[0.25em] text-[#E10600] font-bold">Drop 001 · {product?.edition}</p>
          <h1 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] mt-4" data-testid="product-title">
            <MaskLines lines={["Project X", <span key="m" className="text-[#E10600]">Make Parties</span>, "Great Again Hat"]} delay={0.1} />
          </h1>
          <div className="flex items-end gap-4 mt-8">
            <span className="font-display text-6xl sm:text-7xl" data-testid="product-price">{money(product?.price)}</span>
            <span className="font-bold text-lg pb-2" data-testid="product-shipping">+ {shipText(product?.shipping)} shipping</span>
          </div>
          <p className="mt-8 text-xl sm:text-2xl font-extrabold leading-snug" data-testid="product-description">{product?.description}</p>

          <ul className="mt-8 grid sm:grid-cols-3 gap-3">
            {[[Sparkles, "Red & white colorway"], [ShieldCheck, "Adjustable — one size"], [Truck, "Free shipping across the US"]].map(([Icon, t]) => (
              <li key={t} className="flex items-center gap-3 bg-white border-2 border-[#0B0B0B] px-4 py-3 font-bold text-sm">
                <Icon className="w-5 h-5 text-[#E10600] shrink-0" /> {t}
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Qty value={qty} onChange={setQty} max={max} />
            <div className="font-mono-x text-sm uppercase tracking-wider" data-testid="order-summary">
              {qty} × {money(product?.price)} + {shipText(product?.shipping)} ship = <strong className="text-[#E10600] text-base">{money(subtotal + (product?.shipping ?? 0))}</strong>
            </div>
          </div>
          <BuyButton product={product} quantity={qty} className="mt-8 max-w-xl" />
          <p className="mt-6 font-mono-x text-xs uppercase tracking-[0.2em] text-zinc-600">Secure checkout by Stripe · Visa · Mastercard · Debit · Apple Pay · Google Pay · Cash App Pay</p>
          <p className="mt-3 text-sm font-semibold">
            All sales final — no refunds unless your order arrives damaged.{" "}
            <Link to="/shipping" data-testid="shop-shipping-policy-link" className="underline decoration-[#E10600] decoration-2 underline-offset-4 transition-colors duration-200 hover:text-[#E10600]">Shipping & returns</Link>
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
